import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayer } from '../../../models/data-layer';
import { DropdownModule } from 'primeng/dropdown';
import { NgIf } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { getState } from '@ngrx/signals';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { Button } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DialogModule } from 'primeng/dialog';
import { StyleClassModule } from 'primeng/styleclass';

@Component({
  selector: 'app-layer-dropdown',
  standalone: true,
  imports: [
    DropdownModule,
    NgIf,
    SharedModule,
    FormsModule,
    Button,
    DividerModule,
    OverlayPanelModule,
    DialogModule,
    StyleClassModule,
  ],
  templateUrl: './layer-dropdown.component.html',
  styleUrl: './layer-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LayerDropdownComponent),
      multi: true,
    },
  ],
})
export class LayerDropdownComponent implements OnChanges, ControlValueAccessor {
  readonly layerStore = inject(DataLayerStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly globalStore = inject(GlobalsStore);

  @Input() label = 'Jahr';
  @Input() name = 'year';
  @Input() displayNewYearOption = false;
  @Input() selection: DataLayer | null = null;
  @Input() globalSelector = false;
  @Input() disabled = false;
  @Input() enableEmpty = false;
  @Input() withData = false;
  @Input() withDataInIndicators?: string[];
  @Input() selectedTypeId: string | null = null;
  @Input() isStandalone = false;
  @Output() selectionChange = new EventEmitter<DataLayer>();

  addNewLayerDialog = false;
  touched = false;

  constructor() {
    effect(() => {
      const globalState = getState(this.globalStore);
      const layerState = getState(this.layerStore);
      if (!this.enableEmpty && this.globalSelector) {
        this.selectedTypeId = globalState.selectedType?._id || null;
        this.selection = globalState.selectedLayer;
      }

      if (layerState && layerState.selectedId && this.displayNewYearOption) {
        this.selection = this.layerStore.getById(layerState.selectedId);
        this.onChange(this.selection);
        this.closeLayerForm();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      !this.globalSelector &&
      changes['selectedTypeId'] &&
      changes['selectedTypeId'].currentValue === ''
    ) {
      this.selection = null;
    } else if (
      changes['selectedTypeId'] &&
      changes['selectedTypeId'].currentValue !==
        changes['selectedTypeId'].previousValue
    ) {
      this.selection = this.selection || this.typeLayers[0];
      this.onChange(this.selection);
    }
  }

  get typeLayers(): DataLayer[] {
    const layers = this.selectedTypeId
      ? this.layerStore.groupedByType()[this.selectedTypeId]
      : [];

    let filtered: DataLayer[] = [];
    if (this.withDataInIndicators && this.withDataInIndicators.length) {
      const indicatorIds = this.withDataInIndicators;
      filtered = layers.filter(layer => {
        return this.dataStore.dataLayerAndIndicatorsHasData(
          layer._id,
          indicatorIds
        );
      });
    } else if (this.withData) {
      filtered = layers.filter(layer =>
        this.dataStore.dataLayerHasData(layer._id)
      );
    } else {
      filtered = layers;
    }

    return filtered;
  }

  protected onChange = (value: DataLayer) => {
    setTimeout(() => {
      this.selectionChange.emit(value);
      if (this.globalSelector && value)
        this.globalStore.setSelectedLayer(value);
    }, 300);
  };

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: any): void {
    this.selection = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openLayerForm() {
    this.addNewLayerDialog = true;
    this.globalStore.triggerAtlasEvent('addLayerDialog', {
      dataLayerTypeId: this.selectedTypeId || undefined,
    });
  }

  closeLayerForm() {
    this.addNewLayerDialog = false;
  }
}
